// src/components/dealer/DealerVehiclesTable.js
import React, { useState, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import VehicleAvailabilityToggle from './VehicleAvailabilityToggle';
import { dealerVehiclesAPI } from '../../services/api';

const DealerVehiclesTable = ({ onUploadClick }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('year');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updatingId, setUpdatingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const itemsPerPage = 10;

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      const { data } = await dealerVehiclesAPI.list(params);

      // Handle both paginated and non-paginated responses
      if (data.vehicles) {
        setVehicles(data.vehicles);
        setTotalPages(data.totalPages || 1);
      } else if (Array.isArray(data)) {
        // If backend returns array, handle client-side pagination
        const filtered = data.filter((v) =>
          searchTerm
            ? `${v.year} ${v.make} ${v.model} ${v.trim || ''}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            : true
        );

        // Client-side sorting
        const sorted = [...filtered].sort((a, b) => {
          let aVal = a[sortBy];
          let bVal = b[sortBy];

          if (sortBy === 'price' || sortBy === 'mileage' || sortBy === 'year') {
            aVal = Number(aVal) || 0;
            bVal = Number(bVal) || 0;
          } else {
            aVal = String(aVal || '').toLowerCase();
            bVal = String(bVal || '').toLowerCase();
          }

          if (sortOrder === 'asc') {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });

        const total = Math.ceil(sorted.length / itemsPerPage);
        setTotalPages(total);

        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        setVehicles(sorted.slice(start, end));
      } else {
        setVehicles([]);
      }
    } catch (err) {
      console.error('Failed to fetch vehicles:', err);

      // Check if it's a 404 (endpoint not implemented) - silently load mock data
      const is404 = err.response?.status === 404;

      if (!is404) {
        // Only show error for real failures (not 404s)
        setError(err.response?.data?.message || err.message || 'Failed to load vehicles.');
      }

      // Use mock data if API fails
      const mockVehicles = generateMockVehicles();
      const filtered = mockVehicles.filter((v) =>
        searchTerm
          ? `${v.year} ${v.make} ${v.model} ${v.trim || ''}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          : true
      );

      const sorted = [...filtered].sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];

        if (sortBy === 'price' || sortBy === 'mileage' || sortBy === 'year') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
        }

        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      const total = Math.ceil(sorted.length / itemsPerPage);
      setTotalPages(total);

      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      setVehicles(sorted.slice(start, end));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy, sortOrder]);

  useEffect(() => {
    // Reset to page 1 when search term changes
    if (currentPage === 1) {
      fetchVehicles();
    } else {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleToggleAvailability = async (vehicleId, newAvailability) => {
    try {
      setUpdatingId(vehicleId);
      setError('');
      setSuccessMessage('');

      await dealerVehiclesAPI.updateAvailability(vehicleId, newAvailability);

      // Update local state optimistically
      setVehicles((prev) =>
        prev.map((v) =>
          v._id === vehicleId || v.id === vehicleId
            ? { ...v, availableForTestDrive: newAvailability }
            : v
        )
      );

      setSuccessMessage(
        `Vehicle ${newAvailability ? 'enabled' : 'disabled'} for test drives`
      );
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to update availability:', err);

      // Check if it's a 404 (endpoint not implemented) - update local state anyway
      const is404 = err.response?.status === 404;

      if (is404) {
        // Update local state even though backend doesn't exist
        setVehicles((prev) =>
          prev.map((v) =>
            v._id === vehicleId || v.id === vehicleId
              ? { ...v, availableForTestDrive: newAvailability }
              : v
          )
        );

        // Show success message with mock data indicator
        setSuccessMessage(
          `Vehicle ${newAvailability ? 'enabled' : 'disabled'} for test drives (mock data)`
        );
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        // Only show error for real failures (not 404s)
        setError(
          err.response?.data?.message ||
            err.message ||
            'Failed to update availability. Please try again.'
        );

        // Revert optimistic update on real error
        await fetchVehicles();
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 inline ml-1" />
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  return (
    <div className="space-y-3">
      {/* Header with search and upload button */}
      <div className="flex flex-col lg:flex-row gap-3 justify-between items-start lg:items-center">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-app-text-secondary-light dark:text-app-text-secondary w-4 h-4" />
          <input
            type="text"
            placeholder="Search by make, model, or year..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text text-sm placeholder-app-text-secondary-light dark:placeholder-app-text-secondary focus:outline-none focus:border-app-accent"
          />
        </div>
        <button
          type="button"
          onClick={onUploadClick}
          className="px-4 py-2 rounded-xl bg-app-accent text-black font-semibold text-sm hover:bg-app-accent/90 transition-colors"
        >
          Upload CSV Inventory
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-900/30 text-red-300 px-3 py-2 rounded-xl text-xs">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-900/30 text-green-300 px-3 py-2 rounded-xl text-xs">
          {successMessage}
        </div>
      )}

      {/* Table */}
      <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-app-accent animate-spin" />
            <span className="ml-2 text-sm text-app-text-secondary-light dark:text-app-text-secondary">Loading vehicles...</span>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-12 px-4">
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
              {searchTerm
                ? 'No vehicles found matching your search.'
                : 'No vehicles in inventory. Upload a CSV to get started.'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-app-border-light dark:border-app-border bg-app-bg-tertiary-light dark:bg-app-bg-tertiary">
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary cursor-pointer hover:text-app-text-light dark:hover:text-app-text"
                      onClick={() => handleSort('year')}
                    >
                      Year <SortIcon field="year" />
                    </th>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary cursor-pointer hover:text-app-text-light dark:hover:text-app-text"
                      onClick={() => handleSort('make')}
                    >
                      Make <SortIcon field="make" />
                    </th>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary cursor-pointer hover:text-app-text-light dark:hover:text-app-text"
                      onClick={() => handleSort('model')}
                    >
                      Model <SortIcon field="model" />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary">
                      Trim
                    </th>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary cursor-pointer hover:text-app-text-light dark:hover:text-app-text"
                      onClick={() => handleSort('price')}
                    >
                      Price <SortIcon field="price" />
                    </th>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary cursor-pointer hover:text-app-text-light dark:hover:text-app-text"
                      onClick={() => handleSort('mileage')}
                    >
                      Mileage <SortIcon field="mileage" />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary">
                      Available for Test Drives
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle) => {
                    const vehicleId = vehicle._id || vehicle.id;
                    const isUpdating = updatingId === vehicleId;

                    return (
                      <tr
                        key={vehicleId}
                        className="border-b border-app-border-light dark:border-app-border hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-app-text-light dark:text-app-text">
                          {vehicle.year}
                        </td>
                        <td className="px-4 py-3 text-sm text-app-text-light dark:text-app-text">
                          {vehicle.make}
                        </td>
                        <td className="px-4 py-3 text-sm text-app-text-light dark:text-app-text">
                          {vehicle.model}
                        </td>
                        <td className="px-4 py-3 text-sm text-app-text-secondary-light dark:text-app-text-secondary">
                          {vehicle.trim || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-app-text-light dark:text-app-text">
                          {formatPrice(vehicle.price)}
                        </td>
                        <td className="px-4 py-3 text-sm text-app-text-light dark:text-app-text">
                          {formatMileage(vehicle.mileage)} mi
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <VehicleAvailabilityToggle
                              vehicleId={vehicleId}
                              isAvailable={vehicle.availableForTestDrive}
                              onToggle={handleToggleAvailability}
                              disabled={isUpdating}
                            />
                            {isUpdating && (
                              <Loader2 className="w-3 h-3 text-app-text-secondary-light dark:text-app-text-secondary animate-spin" />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-app-border-light dark:divide-app-border">
              {vehicles.map((vehicle) => {
                const vehicleId = vehicle._id || vehicle.id;
                const isUpdating = updatingId === vehicleId;

                return (
                  <div key={vehicleId} className="p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </div>
                        {vehicle.trim && (
                          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">{vehicle.trim}</div>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-app-accent">
                        {formatPrice(vehicle.price)}
                      </div>
                    </div>
                    <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                      {formatMileage(vehicle.mileage)} miles
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                        Available for test drives
                      </span>
                      <div className="flex items-center gap-2">
                        <VehicleAvailabilityToggle
                          vehicleId={vehicleId}
                          isAvailable={vehicle.availableForTestDrive}
                          onToggle={handleToggleAvailability}
                          disabled={isUpdating}
                        />
                        {isUpdating && (
                          <Loader2 className="w-3 h-3 text-app-text-secondary-light dark:text-app-text-secondary animate-spin" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && vehicles.length > 0 && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg bg-app-bg-tertiary-light dark:bg-app-bg-tertiary text-app-text-light dark:text-app-text text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-app-bg-quaternary-light dark:hover:bg-app-bg-quaternary transition-colors"
          >
            Previous
          </button>
          <span className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded-lg bg-app-bg-tertiary-light dark:bg-app-bg-tertiary text-app-text-light dark:text-app-text text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-app-bg-quaternary-light dark:hover:bg-app-bg-quaternary transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Mock data generator for development
function generateMockVehicles() {
  const makes = ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes-Benz', 'Audi', 'Tesla'];
  const models = {
    Toyota: ['Camry', 'Corolla', 'RAV4', 'Highlander'],
    Honda: ['Accord', 'Civic', 'CR-V', 'Pilot'],
    Ford: ['F-150', 'Mustang', 'Explorer', 'Escape'],
    Chevrolet: ['Silverado', 'Malibu', 'Equinox', 'Tahoe'],
    BMW: ['3 Series', '5 Series', 'X3', 'X5'],
    'Mercedes-Benz': ['C-Class', 'E-Class', 'GLC', 'GLE'],
    Audi: ['A4', 'A6', 'Q5', 'Q7'],
    Tesla: ['Model 3', 'Model Y', 'Model S', 'Model X'],
  };
  const trims = ['Base', 'Sport', 'Limited', 'Premium', 'Luxury'];

  const mockVehicles = [];
  for (let i = 0; i < 25; i++) {
    const make = makes[Math.floor(Math.random() * makes.length)];
    const model = models[make][Math.floor(Math.random() * models[make].length)];
    const year = 2018 + Math.floor(Math.random() * 7);
    const price = 20000 + Math.floor(Math.random() * 60000);
    const mileage = Math.floor(Math.random() * 80000);
    const trim = trims[Math.floor(Math.random() * trims.length)];

    mockVehicles.push({
      id: `mock-${i}`,
      _id: `mock-${i}`,
      year,
      make,
      model,
      trim,
      price,
      mileage,
      availableForTestDrive: Math.random() > 0.3,
      vin: `1HGBH41JXMN${String(i).padStart(6, '0')}`,
    });
  }

  return mockVehicles;
}

export default DealerVehiclesTable;
